import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  BarChart3, TrendingUp, Users, Activity, Calendar, 
  FileText, Download, RefreshCw, Building, Clock
} from 'lucide-react';

const Analytics = () => {
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState({
    overview: {
      totalClientUsers: 0,
      activeUsers: 0,
      totalBusinessUnits: 0,
      totalTickets: 0,
      totalFiles: 0,
      storageUsed: 0
    },
    userActivity: [],
    ticketStats: {},
    storageByBU: []
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/client-portal/analytics?range=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        // Mock data for demo
        setAnalytics({
          overview: {
            totalClientUsers: 45,
            activeUsers: 38,
            totalBusinessUnits: 8,
            totalTickets: 127,
            totalFiles: 234,
            storageUsed: 2.4 * 1024 * 1024 * 1024 // 2.4 GB
          },
          userActivity: [
            { date: '2024-01-01', logins: 23, activeUsers: 18 },
            { date: '2024-01-02', logins: 31, activeUsers: 25 },
            { date: '2024-01-03', logins: 28, activeUsers: 22 },
            { date: '2024-01-04', logins: 35, activeUsers: 28 },
            { date: '2024-01-05', logins: 42, activeUsers: 33 }
          ],
          ticketStats: {
            open: 12,
            in_progress: 8,
            resolved: 95,
            closed: 12,
            byPriority: { high: 5, medium: 18, low: 24 }
          },
          storageByBU: [
            { name: 'Marketing', used: 0.8 * 1024 * 1024 * 1024, limit: 1024 * 1024 * 1024 },
            { name: 'Sales', used: 0.6 * 1024 * 1024 * 1024, limit: 1024 * 1024 * 1024 },
            { name: 'Operations', used: 0.4 * 1024 * 1024 * 1024, limit: 1024 * 1024 * 1024 }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const exportData = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `client-portal-analytics-${dateRange}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-32 bg-slate-700 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1,2].map(i => (
              <div key={i} className="h-64 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Analytics</h1>
          <p className="text-slate-400 mt-1">Client portal usage statistics and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-slate-700 text-slate-200 border border-slate-600 rounded-lg px-3 py-2"
          >
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="this_year">This Year</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Client Users</p>
              <p className="text-2xl font-bold text-slate-100">{analytics.overview.totalClientUsers}</p>
              <p className="text-green-400 text-sm">
                {analytics.overview.activeUsers} active
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Business Units</p>
              <p className="text-2xl font-bold text-slate-100">{analytics.overview.totalBusinessUnits}</p>
              <p className="text-slate-400 text-sm">Active portals</p>
            </div>
            <Building className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Support Tickets</p>
              <p className="text-2xl font-bold text-slate-100">{analytics.overview.totalTickets}</p>
              <p className="text-yellow-400 text-sm">
                {analytics.ticketStats.open + analytics.ticketStats.in_progress} open
              </p>
            </div>
            <FileText className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Files Uploaded</p>
              <p className="text-2xl font-bold text-slate-100">{analytics.overview.totalFiles}</p>
              <p className="text-slate-400 text-sm">
                {formatFileSize(analytics.overview.storageUsed)} used
              </p>
            </div>
            <Activity className="w-8 h-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Charts and Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Activity Chart */}
        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-slate-100">User Activity</h3>
            <TrendingUp className="w-5 h-5 text-blue-400" />
          </div>
          
          <div className="h-48 flex items-end space-x-2">
            {analytics.userActivity.slice(-7).map((day, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="flex flex-col items-center gap-1 mb-2">
                  <div 
                    className="bg-blue-500 w-full rounded-t"
                    style={{ 
                      height: `${Math.max((day.logins / Math.max(...analytics.userActivity.map(d => d.logins))) * 120, 4)}px` 
                    }}
                  ></div>
                  <div 
                    className="bg-green-500 w-full rounded-t"
                    style={{ 
                      height: `${Math.max((day.activeUsers / Math.max(...analytics.userActivity.map(d => d.activeUsers))) * 80, 4)}px` 
                    }}
                  ></div>
                </div>
                <div className="text-xs text-slate-400">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="text-xs text-slate-300">
                  {day.logins}
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex items-center gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span className="text-slate-300">Logins</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-slate-300">Active Users</span>
            </div>
          </div>
        </div>

        {/* Ticket Statistics */}
        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-slate-100">Support Tickets</h3>
            <FileText className="w-5 h-5 text-yellow-400" />
          </div>
          
          {/* Ticket Status Breakdown */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-700/30 p-3 rounded">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Open</span>
                <span className="text-slate-200 font-semibold">{analytics.ticketStats.open}</span>
              </div>
            </div>
            <div className="bg-slate-700/30 p-3 rounded">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">In Progress</span>
                <span className="text-slate-200 font-semibold">{analytics.ticketStats.in_progress}</span>
              </div>
            </div>
            <div className="bg-slate-700/30 p-3 rounded">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Resolved</span>
                <span className="text-slate-200 font-semibold">{analytics.ticketStats.resolved}</span>
              </div>
            </div>
            <div className="bg-slate-700/30 p-3 rounded">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Closed</span>
                <span className="text-slate-200 font-semibold">{analytics.ticketStats.closed}</span>
              </div>
            </div>
          </div>

          {/* Priority Distribution */}
          <div>
            <h4 className="text-slate-300 font-medium mb-2">Priority Distribution</h4>
            <div className="space-y-2">
              {Object.entries(analytics.ticketStats.byPriority || {}).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      priority === 'high' ? 'bg-red-900/30 text-red-400' :
                      priority === 'medium' ? 'bg-yellow-900/30 text-yellow-400' :
                      'bg-green-900/30 text-green-400'
                    }`}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </span>
                  </div>
                  <span className="text-slate-300 font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Storage Usage by Business Unit */}
      <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-slate-100">Storage Usage by Business Unit</h3>
          <BarChart3 className="w-5 h-5 text-green-400" />
        </div>
        
        <div className="space-y-4">
          {analytics.storageByBU.map((bu, index) => {
            const percentage = (bu.used / bu.limit) * 100;
            return (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-300">{bu.name}</span>
                  </div>
                  <span className="text-slate-400 text-sm">
                    {formatFileSize(bu.used)} / {formatFileSize(bu.limit)}
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2 mb-1">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      percentage > 90 ? 'bg-red-500' : 
                      percentage > 75 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-400">
                  {percentage.toFixed(1)}% used
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-6 flex items-center justify-center text-slate-400 text-sm">
        <Clock className="w-4 h-4 mr-2" />
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
};

export default Analytics;